<template>
  <AuthLayout>
    <!-- Step 1: 폼 입력 -->
    <div v-if="step === 1">
      <div class="forgot-head">
        <div class="forgot-badge">비밀번호 재설정</div>
        <h1 class="forgot-title">Reset Password</h1>
      </div>

      <p class="description">
        가입하신 휴대폰 번호를 입력해주세요.<br />
        비밀번호 재설정 링크를 문자로 보내드립니다.
      </p>

      <form class="forgot-form" @submit.prevent="handleSubmit">
        <UiInput
          v-model="phoneNumber"
          type="tel"
          inputmode="tel"
          placeholder="휴대폰 번호 ( - 제외)"
          autocomplete="tel"
        />

        <UiButton
          type="submit"
          :disabled="!canSubmit || loading"
          :loading="loading"
        >
          문자 보내기
        </UiButton>
      </form>
    </div>

    <!-- Step 2: 성공 화면 -->
    <div v-else class="success-content">
      <div class="success-icon">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="50" fill="#2A4624" />
          <circle cx="50" cy="50" r="36" fill="#78FF47" />
          <path
            d="M35 50 L45 60 L65 40"
            stroke="#000000"
            stroke-width="5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="success-title">링크가 전송되었습니다!</h2>
      <p class="success-message">
        <span class="phone-highlight">{{ formatPhone(phoneNumber) }}</span
        >로<br />
        비밀번호 재설정 링크를 보내드렸습니다.<br />
        문자를 확인해주세요.
      </p>

      <div class="success-actions">
        <UiButton @click="goToSignIn">로그인으로 돌아가기</UiButton>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import { api } from "@/lib/api";
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AuthLayout from "@/components/AuthLayout.vue";

export default {
  name: "ForgotPasswordView",
  components: { UiButton, UiInput, AuthLayout },
  data() {
    return {
      step: 1,
      phoneNumber: "",
      loading: false,
    };
  },
  computed: {
    phoneValid() {
      const digits = (this.phoneNumber || "").replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 11;
    },
    canSubmit() {
      return this.phoneNumber && this.phoneValid;
    },
  },
  methods: {
    async handleSubmit() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;

      try {
        await api("/v1/auth/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number: this.phoneNumber.replace(/\D/g, ""),
          }),
        });

        this.step = 2;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push("/signin");
    },
    formatPhone(phone) {
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
  },
};
</script>

<style scoped>
/* 제목 */
.forgot-head {
  margin-bottom: 24px;
  text-align: left;
}

.forgot-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.forgot-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 설명 */
.description {
  margin: 0 0 32px 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
}

/* 폼 */
.forgot-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* UiInput */
.forgot-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}

.forgot-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.forgot-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.forgot-form .ui-btn {
  width: 100%;
}

/* 하단 링크 */
.forgot-links {
  margin-top: 24px;
  text-align: center;
}

.forgot-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
}

.forgot-link:hover {
  color: #78ff47;
}

/* 성공 화면 */
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
}

.success-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.success-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #fff;
}

.success-message {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.5;
}

.phone-highlight {
  color: #78ff47;
  font-weight: 600;
}

.success-actions {
  width: 100%;
  margin-top: 8px;
}

.success-actions .ui-btn {
  width: 100%;
}

@media (max-width: 640px) {
  .forgot-title {
    font-size: 32px;
  }

  .success-title {
    font-size: 20px;
  }

  .success-message {
    font-size: 14px;
  }
}
</style>
